// Alertas do Waze desenhados como ícones no NOSSO mapa (pedido 2026-09-22:
// "eu quero que os ícones apareçam em cima do meu mapa, não um mapa novo").
// Dados vêm do backend (GET /api/v1/waze/alerts — proxy com cache, ver
// gpsdrive-backend src/waze/waze.service.ts). Aqui: tipo → categoria →
// ícone (SVG gerado, sem arquivo pra baixar) + textos em pt-BR.
import { useCallback, useState } from "react";

export interface WazeAlert {
  id: string;
  lat: number;
  lng: number;
  type: string;
  subtype: string;
  street: string | null;
  city: string | null;
  pubMillis: number | null;
  thumbsUp: number;
}

export interface WazeAlertsResponse {
  ok: boolean;
  tooWide: boolean;
  alerts: WazeAlert[];
}

type Category =
  | "buraco"
  | "obra"
  | "interdicao"
  | "acidente"
  | "policia"
  | "parado"
  | "animal"
  | "semaforo"
  | "alagamento"
  | "perigo";

const LABELS: Record<string, string> = {
  HAZARD_ON_ROAD_POT_HOLE: "Buraco na via",
  HAZARD_ON_ROAD_CONSTRUCTION: "Obra na via",
  HAZARD_ON_ROAD_LANE_CLOSED: "Faixa interditada",
  HAZARD_ON_ROAD_OBJECT: "Objeto na pista",
  HAZARD_ON_ROAD_CAR_STOPPED: "Veículo parado na via",
  HAZARD_ON_SHOULDER_CAR_STOPPED: "Veículo parado no acostamento",
  HAZARD_ON_SHOULDER_ANIMALS: "Animal na pista",
  HAZARD_ON_ROAD_ROAD_KILL: "Animal atropelado na pista",
  HAZARD_ON_ROAD_TRAFFIC_LIGHT_FAULT: "Semáforo com defeito",
  HAZARD_ON_ROAD_ICE: "Pista escorregadia",
  HAZARD_ON_ROAD_OIL: "Óleo na pista",
  HAZARD_WEATHER_FLOOD: "Alagamento",
  HAZARD_WEATHER_FOG: "Neblina",
  HAZARD_WEATHER_HEAVY_RAIN: "Chuva forte",
  HAZARD_WEATHER_HAIL: "Granizo",
  HAZARD_ON_ROAD: "Perigo na via",
  HAZARD_ON_SHOULDER: "Perigo no acostamento",
  ACCIDENT_MINOR: "Acidente leve",
  ACCIDENT_MAJOR: "Acidente grave",
  POLICE_HIDING: "Polícia (escondida)",
  POLICE_VISIBLE: "Polícia",
  ROAD_CLOSED_EVENT: "Via interditada (evento)",
  ROAD_CLOSED_CONSTRUCTION: "Via interditada (obra)",
  ROAD_CLOSED_HAZARD: "Via interditada (perigo)",
};

const TYPE_LABELS: Record<string, string> = {
  ACCIDENT: "Acidente",
  POLICE: "Polícia",
  ROAD_CLOSED: "Via interditada",
  HAZARD: "Perigo na via",
};

export function wazeAlertLabel(a: WazeAlert): string {
  return LABELS[a.subtype] ?? TYPE_LABELS[a.type] ?? "Alerta";
}

function categoryOf(a: WazeAlert): Category {
  const s = a.subtype;
  if (a.type === "ACCIDENT") return "acidente";
  if (a.type === "POLICE") return "policia";
  if (a.type === "ROAD_CLOSED" || s === "HAZARD_ON_ROAD_LANE_CLOSED") return "interdicao";
  if (s === "HAZARD_ON_ROAD_POT_HOLE") return "buraco";
  if (s === "HAZARD_ON_ROAD_CONSTRUCTION") return "obra";
  if (s.includes("CAR_STOPPED")) return "parado";
  if (s.includes("ANIMALS") || s.includes("ROAD_KILL")) return "animal";
  if (s === "HAZARD_ON_ROAD_TRAFFIC_LIGHT_FAULT") return "semaforo";
  if (s === "HAZARD_WEATHER_FLOOD") return "alagamento";
  return "perigo";
}

// Visual no estilo do Waze (pedido 2026-09-22: "coloca igualzinho do
// Waze") — desenhos PRÓPRIOS, nunca os arquivos do Waze (arte da marca):
// balão redondo com anel branco grosso, sombra suave e rabinho embaixo,
// fundo chapado (amarelo = perigos, azul = polícia, cinza = obra...) e
// símbolo cartunesco com contorno escuro. 72×80, ponta do rabinho em
// (36,74) = âncora no ponto exato do alerta. Símbolo desenhado em volta de
// (0,0), deslocado pro centro do círculo (36,34).
const BADGE = (bg: string, glyph: string) =>
  `<svg xmlns='http://www.w3.org/2000/svg' width='72' height='80' viewBox='0 0 72 80'>` +
  `<defs><filter id='s' x='-20%' y='-20%' width='140%' height='140%'><feDropShadow dx='0' dy='2' stdDeviation='2.2' flood-color='#000' flood-opacity='0.35'/></filter></defs>` +
  `<g filter='url(#s)'><path d='M29 62h14l-7 12z' fill='white'/><circle cx='36' cy='34' r='31' fill='white'/></g>` +
  `<circle cx='36' cy='34' r='26' fill='${bg}'/>` +
  `<g transform='translate(36 34)' stroke-linejoin='round' stroke-linecap='round'>${glyph}</g>` +
  `</svg>`;

const Y = "#ffc62b"; // amarelo "perigo"
const BR = "#6b4200"; // contorno marrom dos desenhos em fundo amarelo

// Placa de atenção (triângulo com "!") num poste — reaproveitada em vários.
const SIGN = (dy: number, scale = 1) =>
  `<g transform='translate(0 ${dy}) scale(${scale})'>` +
  `<path d='M0 -11 L10 6 H-10 Z' fill='#ffe9a6' stroke='${BR}' stroke-width='2.2'/>` +
  `<path d='M0 -4.5 V0.5' stroke='${BR}' stroke-width='2.4'/><circle cx='0' cy='3.2' r='1.3' fill='${BR}'/>` +
  `</g>`;

const GLYPHS: Record<Category, [string, string]> = {
  // Buraco: placa de atenção sobre a cratera.
  buraco: [
    Y,
    `<path d='M0 -2 V9' stroke='${BR}' stroke-width='2.4'/>` +
      SIGN(-9) +
      `<ellipse cx='0' cy='11.5' rx='14' ry='5' fill='#8a6d4b' stroke='${BR}' stroke-width='1.8'/>` +
      `<ellipse cx='0' cy='12' rx='9' ry='2.8' fill='#3b2a18'/>`,
  ],
  // Obra: cone laranja listrado, fundo cinza claro.
  obra: [
    "#e3e6ea",
    `<path d='M-5.5 -16 H5.5 L12 9 H-12 Z' fill='#fb8c3c' stroke='#9a3412' stroke-width='2'/>` +
      `<path d='M-3.9 -9 H3.9 L5.2 -4 H-5.2 Z M-6.6 1 H6.6 L7.6 5 H-7.6 Z' fill='white'/>` +
      `<rect x='-16' y='9' width='32' height='6' rx='2.5' fill='#fb8c3c' stroke='#9a3412' stroke-width='2'/>`,
  ],
  // Via interditada: cavalete listrado vermelho e branco.
  interdicao: [
    "#ff5b5b",
    `<path d='M-10 -1 V15 M10 -1 V15' stroke='#7f1d1d' stroke-width='2.6'/>` +
      `<rect x='-17' y='-12' width='34' height='12' rx='2.5' fill='white' stroke='#7f1d1d' stroke-width='2'/>` +
      `<path d='M-11 -12 L-5 -12 L-10 0 L-16 0 Z M1 -12 L7 -12 L2 0 L-4 0 Z M13 -12 L16 -12 L16 -5 L14 0 L8 0 Z' fill='#e11d48'/>` +
      `<rect x='-17' y='-12' width='34' height='12' rx='2.5' fill='none' stroke='#7f1d1d' stroke-width='2'/>`,
  ],
  // Acidente: dois carros batendo com estrela de impacto.
  acidente: [
    "#ff7a59",
    `<path d='M0 -18 L3 -11 L10 -13 L6 -7 L12 -3 L4 -3 L3 4 L-1 -2 L-7 2 L-5 -5 L-12 -8 L-4 -10 Z' fill='#ffe066' stroke='#92400e' stroke-width='1.6'/>` +
      `<g transform='translate(-9 7) rotate(-18)'><rect x='-8' y='-3' width='16' height='7' rx='2.5' fill='#60a5fa' stroke='#1e3a8a' stroke-width='1.6'/><rect x='-4' y='-7' width='8' height='5' rx='1.8' fill='#bfdbfe' stroke='#1e3a8a' stroke-width='1.4'/><circle cx='-4.5' cy='4.5' r='2.2' fill='#1f2937'/><circle cx='4.5' cy='4.5' r='2.2' fill='#1f2937'/></g>` +
      `<g transform='translate(9 7) rotate(18)'><rect x='-8' y='-3' width='16' height='7' rx='2.5' fill='#fde047' stroke='#92400e' stroke-width='1.6'/><rect x='-4' y='-7' width='8' height='5' rx='1.8' fill='#fef9c3' stroke='#92400e' stroke-width='1.4'/><circle cx='-4.5' cy='4.5' r='2.2' fill='#1f2937'/><circle cx='4.5' cy='4.5' r='2.2' fill='#1f2937'/></g>`,
  ],
  // Polícia: rosto com quepe azul.
  policia: [
    "#27a8f5",
    `<circle cx='0' cy='6' r='11' fill='#f2b98a' stroke='#7a4a2a' stroke-width='1.8'/>` +
      `<circle cx='-4' cy='5' r='1.4' fill='#3b2412' stroke='none'/><circle cx='4' cy='5' r='1.4' fill='#3b2412' stroke='none'/>` +
      `<path d='M-4 10 Q0 13 4 10' fill='none' stroke='#7a4a2a' stroke-width='1.6'/>` +
      `<path d='M-14 -3 C-14 -17 14 -17 14 -3 Z' fill='#1e40af' stroke='#0b1f5c' stroke-width='1.8'/>` +
      `<path d='M-15.5 -3 H15.5 Q14 2 0 2 Q-14 2 -15.5 -3 Z' fill='#0b1f5c'/>` +
      `<circle cx='0' cy='-9' r='2.6' fill='#fde047' stroke='#a16207' stroke-width='1'/>`,
  ],
  // Veículo parado: carro com pisca-alerta.
  parado: [
    Y,
    `<path d='M-15 8 V1 L-10 -7 H10 L15 1 V8 Z' fill='#e5e7eb' stroke='${BR}' stroke-width='2'/>` +
      `<path d='M-8 -5 H8 L11 0 H-11 Z' fill='#bae6fd' stroke='${BR}' stroke-width='1.4'/>` +
      `<circle cx='-9' cy='9' r='3.4' fill='#374151' stroke='${BR}' stroke-width='1.4'/><circle cx='9' cy='9' r='3.4' fill='#374151' stroke='${BR}' stroke-width='1.4'/>` +
      `<path d='M0 -18 L5 -10 H-5 Z' fill='#ef4444' stroke='${BR}' stroke-width='1.4'/>`,
  ],
  // Animal na pista: pegada.
  animal: [
    Y,
    `<path d='M-8 9 C-8 1 8 1 8 9 C8 14 -8 14 -8 9 Z' fill='#8b5a2b' stroke='${BR}' stroke-width='1.8'/>` +
      `<ellipse cx='-11' cy='-2' rx='3.2' ry='4.2' fill='#8b5a2b' stroke='${BR}' stroke-width='1.6'/>` +
      `<ellipse cx='-4' cy='-8' rx='3.2' ry='4.2' fill='#8b5a2b' stroke='${BR}' stroke-width='1.6'/>` +
      `<ellipse cx='4' cy='-8' rx='3.2' ry='4.2' fill='#8b5a2b' stroke='${BR}' stroke-width='1.6'/>` +
      `<ellipse cx='11' cy='-2' rx='3.2' ry='4.2' fill='#8b5a2b' stroke='${BR}' stroke-width='1.6'/>`,
  ],
  // Semáforo com defeito.
  semaforo: [
    Y,
    `<rect x='-7.5' y='-17' width='15' height='30' rx='5' fill='#374151' stroke='#111827' stroke-width='2'/>` +
      `<circle cx='0' cy='-9.5' r='3.4' fill='#ef4444'/><circle cx='0' cy='-2' r='3.4' fill='#6b7280'/><circle cx='0' cy='5.5' r='3.4' fill='#22c55e'/>` +
      `<path d='M0 13 V17' stroke='#111827' stroke-width='2.4'/>`,
  ],
  // Alagamento: ondas.
  alagamento: [
    "#35b6f0",
    `<path d='M0 -17 C5 -10 8 -6 8 -2 A8 8 0 0 1 -8 -2 C-8 -6 -5 -10 0 -17 Z' fill='#e0f2fe' stroke='#075985' stroke-width='1.8'/>` +
      `<path d='M-16 8 Q-12 4 -8 8 T0 8 T8 8 T16 8' fill='none' stroke='white' stroke-width='3'/>` +
      `<path d='M-16 14 Q-12 10 -8 14 T0 14 T8 14 T16 14' fill='none' stroke='#e0f2fe' stroke-width='3'/>`,
  ],
  // Perigo genérico: placa de atenção grande.
  perigo: [Y, `<path d='M0 4 V15' stroke='${BR}' stroke-width='2.6'/>` + SIGN(-3, 1.35)],
};

const iconCache = new Map<Category, string>();
export function wazeAlertIconUrl(a: WazeAlert): string {
  const cat = categoryOf(a);
  let url = iconCache.get(cat);
  if (!url) {
    const [bg, glyph] = GLYPHS[cat];
    url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(BADGE(bg, glyph))}`;
    iconCache.set(cat, url);
  }
  return url;
}

export function wazeAlertColor(a: WazeAlert): string {
  return GLYPHS[categoryOf(a)][0];
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function timeAgo(ms: number): string {
  const min = Math.max(0, Math.round((Date.now() - ms) / 60000));
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `há ${h} h`;
  return `há ${Math.round(h / 24)} d`;
}

// Conteúdo do balão ao tocar num ícone (InfoWindow do Google Maps).
export function wazeAlertPopupHtml(a: WazeAlert): string {
  const where = [a.street, a.city].filter(Boolean).join(" · ");
  const meta = [
    a.pubMillis ? `Reportado ${timeAgo(a.pubMillis)}` : null,
    a.thumbsUp > 0 ? `👍 ${a.thumbsUp} ${a.thumbsUp === 1 ? "confirmação" : "confirmações"}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    `<div style="font-family:system-ui,sans-serif;min-width:170px;max-width:240px;color:#0f172a">` +
    `<div style="display:flex;align-items:center;gap:6px;font-weight:700;font-size:13px">` +
    `<span style="width:10px;height:10px;border-radius:999px;background:${wazeAlertColor(a)};flex:none"></span>` +
    `${escapeHtml(wazeAlertLabel(a))}</div>` +
    (where ? `<div style="margin-top:3px;font-size:12px;color:#334155">${escapeHtml(where)}</div>` : "") +
    (meta ? `<div style="margin-top:3px;font-size:11px;color:#64748b">${escapeHtml(meta)}</div>` : "") +
    `<div style="margin-top:5px;font-size:10px;color:#94a3b8">Fonte: comunidade Waze</div>` +
    `</div>`
  );
}

// Liga/desliga da camada (menu do olho/camadas), lembrado no aparelho.
// Desligado por padrão — é informação extra, não do rastreamento.
const TOGGLE_KEY = "gpsdrive.map.showWazeAlerts";
export function useWazeAlertsToggle(): [boolean, (v: boolean) => void] {
  const [on, setOn] = useState(() => {
    try {
      return localStorage.getItem(TOGGLE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const set = useCallback((v: boolean) => {
    setOn(v);
    try {
      localStorage.setItem(TOGGLE_KEY, v ? "1" : "0");
    } catch {
      // só não lembra a escolha
    }
  }, []);
  return [on, set];
}
